import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, BRAND } from "../lib/config";
import { Button } from "../components/ui/button";
import {
  Loader2, CheckCircle2, ShieldCheck, Lock, ArrowLeft,
  Phone, MessageSquare, Sparkles, Apple, CreditCard,
} from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const cached = sessionStorage.getItem("last_booking");
    if (cached) {
      try {
        const b = JSON.parse(cached);
        if (b.booking_id === bookingId) {
          setBooking(b);
          setLoading(false);
          return;
        }
      } catch { /* ignore */ }
    }
    api.get(`/bookings/${bookingId}`)
      .then(({ data }) => setBooking(data))
      .catch(() => setBooking(null))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const payWithStripe = async () => {
    setRedirecting(true);
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post("/payments/checkout/session", {
        booking_id: bookingId,
        origin_url,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setRedirecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-[#10B981]" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-600">Booking not found.</p>
        <Button onClick={() => navigate("/")} className="mt-4 rounded-full bg-[#047857] hover:bg-[#065F46]">
          Back to home
        </Button>
      </div>
    );
  }

  const paid = booking.payment_status === "paid";
  const total = Number(booking.price || 0);
  const amountStr = total.toFixed(2);

  return (
    <div className="min-h-screen bg-[#F4F6F5] pb-32 md:pb-12" data-testid="checkout-page">
      {/* === HERO (dark green gradient, branded) === */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#062818] via-[#0A3D27] to-[#062818] text-white">
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, #10B981 0, transparent 40%), radial-gradient(circle at 80% 60%, #34D399 0, transparent 40%)",
        }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-6 pb-10">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-emerald-100/80 hover:text-white text-sm font-semibold mb-5"
            data-testid="checkout-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-300 font-extrabold">
                Secure Checkout
              </div>
              <h1 className="mt-2 text-3xl sm:text-5xl font-extrabold tracking-tighter font-[Outfit] leading-[1.05]">
                Pay <span className="text-[#34D399]">${amountStr}</span>
              </h1>
              <div className="mt-2 text-sm text-emerald-100/80">
                {booking.service_name} · {booking.preferred_date} · {booking.preferred_time}
              </div>
            </div>
            <div className="hidden sm:flex flex-col items-end gap-1">
              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-200/90">
                <Lock className="w-3.5 h-3.5" /> 256-bit encrypted
              </div>
              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-200/90">
                <ShieldCheck className="w-3.5 h-3.5" /> PCI compliant
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-6 grid lg:grid-cols-3 gap-5">
        {/* === Payment options column === */}
        <div className="lg:col-span-2 space-y-5">
          {/* Paid state banner */}
          {paid && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3" data-testid="checkout-paid-banner">
              <CheckCircle2 className="w-6 h-6 text-[#047857]" />
              <div>
                <div className="font-extrabold text-[#047857]">Already paid</div>
                <div className="text-xs text-emerald-800/80">Thank you! Your booking is confirmed.</div>
              </div>
            </div>
          )}

          {/* === Apple Pay / Card via Stripe === */}
          <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="p-5 sm:p-6 pb-3">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#047857] font-extrabold">
                <Sparkles className="w-3.5 h-3.5" /> Recommended
              </div>
              <h2 className="mt-1 text-xl font-extrabold tracking-tight text-gray-900 font-[Outfit]">
                Apple Pay · Google Pay · Card
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                One tap. Secure checkout via Stripe. Apple Pay & Google Pay auto-detected on supported devices.
              </p>
            </div>

            <div className="px-5 sm:px-6 pb-5 sm:pb-6">
              {/* Apple Pay primary button — black, native iOS look */}
              <button
                onClick={payWithStripe}
                disabled={redirecting || paid}
                className="group relative w-full h-14 rounded-2xl bg-black text-white font-bold tracking-tight text-base flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.99] transition shadow-[0_8px_24px_rgba(0,0,0,0.18)]"
                data-testid="pay-applepay-btn"
              >
                {redirecting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Apple className="w-5 h-5 -mt-0.5" fill="currentColor" />
                    <span>Pay</span>
                  </>
                )}
              </button>

              {/* Or divider */}
              <div className="my-3 flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-gray-400 font-bold">
                <div className="flex-1 h-px bg-gray-200" /> or <div className="flex-1 h-px bg-gray-200" />
              </div>

              {/* Card button — premium green */}
              <button
                onClick={payWithStripe}
                disabled={redirecting || paid}
                className="w-full h-14 rounded-2xl bg-gradient-to-br from-[#10B981] to-[#047857] hover:from-[#34D399] hover:to-[#065F46] text-white font-extrabold text-base flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.99] transition shadow-[0_8px_24px_rgba(4,120,87,0.28)]"
                data-testid="pay-stripe-btn"
              >
                {redirecting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" /> Pay with card · ${amountStr}
                  </>
                )}
              </button>

              <div className="mt-3 flex flex-wrap items-center justify-center gap-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">
                <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Stripe</span>
                <span>·</span>
                <span>Visa</span>
                <span>·</span>
                <span>Mastercard</span>
                <span>·</span>
                <span>Amex</span>
                <span>·</span>
                <span>Apple Pay</span>
                <span>·</span>
                <span>Google Pay</span>
              </div>
            </div>
          </div>

          {/* iter76: Direct-pay QR section (Zelle / Venmo / Cash App / Apple
              Cash) is hidden from customers. All customer payments now go
              through Stripe. Offline methods remain available in the admin
              CRM for manual reconciliation. */}

          {/* === Help footer === */}
          <div className="flex flex-col sm:flex-row gap-2.5 sm:items-center sm:justify-between bg-[#062818] text-white rounded-3xl p-5">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-300 font-extrabold">Need help?</div>
              <div className="mt-1 text-sm text-emerald-100/90">Call or text JDSN — we usually reply in minutes.</div>
            </div>
            <div className="flex gap-2">
              <a href={`tel:${BRAND.phoneTel}`} className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full bg-white text-[#062818] font-bold text-sm" data-testid="checkout-call">
                <Phone className="w-4 h-4" /> Call
              </a>
              <a href={`sms:${BRAND.phoneTel}`} className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full border border-emerald-400/40 text-white font-bold text-sm" data-testid="checkout-text">
                <MessageSquare className="w-4 h-4" /> Text us
              </a>
            </div>
          </div>
        </div>

        {/* === Order summary === */}
        <aside className="bg-white rounded-3xl border border-gray-200 p-5 sm:p-6 h-fit lg:sticky lg:top-24 shadow-sm" data-testid="checkout-summary">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#047857] font-extrabold">Order summary</div>
          <div className="mt-2 text-lg font-extrabold text-gray-900 font-[Outfit]">{booking.service_name}</div>
          <div className="text-sm text-gray-600">{booking.preferred_date} · {booking.preferred_time}</div>
          <div className="text-sm text-gray-600 truncate">{booking.address}, {booking.area}</div>

          {/* iter147 — Only show the calc breakdown when its line items sum
              to the LOCKED booking.price within a cent. If the breakdown
              was computed at estimate time from different inputs (mowable
              vs lot sqft, area multipliers, etc.), showing it here would
              contradict the locked price and confuse the customer with
              two different totals. In that case we render nothing — the
              single "Total due" figure below is the only price shown. */}
          {(() => {
            const items = booking.calc?.breakdown;
            if (!Array.isArray(items) || items.length === 0) return null;
            const sum = items.reduce((s, b) => s + Number(b?.amount || 0), 0);
            const locked = Number(booking.price || 0);
            const matches = Math.abs(sum - locked) < 0.02;
            if (!matches) return null;
            return (
              <div className="mt-4 border-t border-gray-100 pt-4 space-y-1.5 text-sm">
                {items.map((b, i) => (
                  <div key={i} className="flex justify-between text-gray-700">
                    <span className="truncate pr-2">{b.label}</span>
                    <span className="font-semibold tabular-nums whitespace-nowrap">${Number(b.amount).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            );
          })()}

          <div className="mt-4 border-t border-gray-100 pt-4 flex items-center justify-between">
            <span className="font-extrabold text-gray-900">Total due</span>
            <span className="text-3xl font-extrabold text-[#047857] tabular-nums font-[Outfit]" data-testid="checkout-total">${amountStr}</span>
          </div>

          {paid && (
            <div className="mt-4 inline-flex items-center gap-2 text-sm font-extrabold text-emerald-700 bg-emerald-50 px-3 py-2 rounded-full">
              <CheckCircle2 className="w-4 h-4" /> Paid
            </div>
          )}

          <Link to="/dashboard" className="mt-5 block text-center text-xs font-bold text-gray-500 hover:text-gray-800">
            View your bookings →
          </Link>
        </aside>
      </div>

      {/* === Sticky mobile bottom bar === */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-200 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]" data-testid="checkout-mobile-bar">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-bold">Total</div>
            <div className="text-xl font-extrabold text-[#047857] tabular-nums leading-tight">${amountStr}</div>
          </div>
          <button
            onClick={payWithStripe}
            disabled={redirecting || paid}
            className="h-12 px-5 rounded-full bg-gradient-to-br from-[#10B981] to-[#047857] text-white font-extrabold text-sm flex items-center gap-2 disabled:opacity-50 active:scale-95 transition shadow-lg"
            data-testid="checkout-mobile-pay-btn"
          >
            {redirecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Apple className="w-4 h-4" fill="currentColor" />}
            {paid ? "Paid" : "Pay now"}
          </button>
        </div>
      </div>
    </div>
  );
}
